import React from 'react';
import {
  DashboardChart,
  MetricCard,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Stack,
  Grid
} from '@tonic/shared/components/ui';
import { Text } from '@tonic/shared/components/ui';
import { Button } from '@tonic/shared/components/ui';
import { Alert, AlertTitle, AlertDescription } from '@tonic/shared/components/ui';
import { LoadingSpinner } from '@tonic/shared/components/ui';
import { useQuery } from '@tanstack/react-query';
import { fetchGraphQL } from '../../../../lib/graphql';
import { RefreshCw, Users, ShoppingBag, Package, Wrench, Repeat, Droplet, Recycle, Brain, TrendingUp, Star, Clock, Leaf, Boxes, AlertTriangle, DollarSign, UserCheck } from 'lucide-react';
import { mockProducts } from '../../../products/mock-data';

const GET_DASHBOARD_METRICS = `
  query GetDashboardMetrics($timeRange: TimeRange!) {
    dashboardData(timeRange: $timeRange) {
      metrics {
        totalUsers {
          current
          previous
          trend
          percentageChange
          comparisonPeriod
        }
        revenue {
          current
          previous
          trend
          percentageChange
          comparisonPeriod
        }
        totalOrders {
          current
          previous
          trend
          percentageChange
          comparisonPeriod
        }
        averageOrderValue {
          current
          previous
          trend
          percentageChange
          comparisonPeriod
        }
        customerRetentionRate {
          current
          previous
          trend
          percentageChange
          comparisonPeriod
        }
      }
      returnsOverTime {
        data {
          timestamp
          value
          label
        }
        total
        averagePerDay
        maxValue
        minValue
        lastUpdated
      }
      lifecycleDistribution {
        stages {
          name
          count
          percentage
          trend
          percentageChange
        }
        total
        mostActive
        lastUpdated
      }
      recentActivities {
        id
        type
        title
        description
        timestamp
        metadata
        user {
          id
          name
          email
        }
        itemId
        itemName
        location
        status
        link
      }
    }
  }
`;

const valueFormatter = (number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(number);

const percentFormatter = (number) =>
  new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(number / 100);

// Simplified mock data to match server schema
const MOCK_DATA = {
  dashboardData: {
    metrics: {
      totalUsers: {
        current: 15000,
        previous: 14000,
        trend: 'up',
        percentageChange: 7.14,
        comparisonPeriod: 'month'
      },
      revenue: {
        current: 283000,
        previous: 250000,
        trend: 'up',
        percentageChange: 13.2,
        comparisonPeriod: 'month'
      },
      totalOrders: {
        current: 2500,
        previous: 2200,
        trend: 'up',
        percentageChange: 13.6,
        comparisonPeriod: 'month'
      },
      averageOrderValue: {
        current: 113.2,
        previous: 113.6,
        trend: 'down',
        percentageChange: -0.35,
        comparisonPeriod: 'month'
      },
      customerRetentionRate: {
        current: 85,
        previous: 82,
        trend: 'up',
        percentageChange: 3.66,
        comparisonPeriod: 'month'
      }
    },
    returnsOverTime: {
      data: [
        { timestamp: '2024-01', value: 150, label: 'Returns' },
        { timestamp: '2024-02', value: 145, label: 'Returns' },
        { timestamp: '2024-03', value: 160, label: 'Returns' }
      ],
      total: 455,
      averagePerDay: 15.2,
      maxValue: 160,
      minValue: 145,
      lastUpdated: '2024-03-20T14:30:00Z'
    },
    lifecycleDistribution: {
      stages: [
        { name: 'New', count: 2500, percentage: 25, trend: 'up', percentageChange: 5 },
        { name: 'Active', count: 5000, percentage: 50, trend: 'stable', percentageChange: 0 },
        { name: 'At Risk', count: 1500, percentage: 15, trend: 'down', percentageChange: -2 },
        { name: 'Churned', count: 1000, percentage: 10, trend: 'down', percentageChange: -3 }
      ],
      total: 10000,
      mostActive: 'Active',
      lastUpdated: '2024-03-20T14:30:00Z'
    },
    recentActivities: [
      {
        id: 'act-1',
        type: 'repair',
        title: 'Repair Service',
        description: 'Completed desk repair service',
        timestamp: '2024-03-20T14:30:00Z',
        metadata: { serviceType: 'repair', itemType: 'desk' },
        user: { id: 'user-1', name: 'John Smith', email: 'john@example.com' },
        itemId: 'item-1',
        itemName: 'MALM Desk',
        location: 'Store #123',
        status: 'completed',
        link: '/admin/re-commerce/repairs/1'
      },
      {
        id: 'act-2',
        type: 'rental',
        title: 'New Rental',
        description: 'New office furniture rental',
        timestamp: '2024-03-20T13:45:00Z',
        metadata: { rentalDuration: '3 months', itemType: 'furniture' },
        user: { id: 'user-2', name: 'Jane Doe', email: 'jane@example.com' },
        itemId: 'item-2',
        itemName: 'MARKUS Office Chair',
        location: 'Store #123',
        status: 'active',
        link: '/admin/re-commerce/rental/2'
      }
    ]
  }
};

export function GlobalOverviewDashboard({ timeRange = 'MONTH' }) {
  const { data, isLoading: loading, error, refetch } = useQuery({
    queryKey: ['dashboardMetrics', timeRange],
    queryFn: () => fetchGraphQL(GET_DASHBOARD_METRICS, { timeRange }),
    placeholderData: MOCK_DATA
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error Loading Dashboard</AlertTitle>
        <AlertDescription className="mt-2">
          <Stack space={4}>
            <Text>Failed to load dashboard data. Please try again later.</Text>
            <Button variant="outline" onClick={refetch}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </Stack>
        </AlertDescription>
      </Alert>
    );
  }

  const {
    metrics,
    returnsOverTime,
    lifecycleDistribution,
    recentActivities
  } = data?.dashboardData || MOCK_DATA.dashboardData;

  // Use mock products data directly
  const products = mockProducts;

  // Calculate product metrics
  const totalProducts = products.length;
  const reCommerceProducts = products.filter(p =>
    p.reCommerceAttributes.eligibleForResale ||
    p.reCommerceAttributes.eligibleForRepair ||
    p.reCommerceAttributes.eligibleForRental ||
    p.reCommerceAttributes.isRefillable
  ).length;
  const lowStockProducts = products.filter(p => p.stock < 20).length;

  // Calculate category distribution
  const categoryData = products.reduce((acc, product) => {
    acc[product.category] = (acc[product.category] || 0) + 1;
    return acc;
  }, {});

  const formattedCategoryData = Object.entries(categoryData).map(([name, value]) => ({
    name,
    value
  }));

  return (
    <Stack space={6} className="pt-2">
      {/* Primary Business Metrics */}
      <Grid cols={1} mdCols={2} lgCols={4} gap={4}>
        <MetricCard
          title="Total Revenue"
          value={valueFormatter(metrics.revenue.current)}
          description="Last 30 days"
          icon={DollarSign}
          trend={metrics.revenue.percentageChange}
        />
        <MetricCard
          title="Total Orders"
          value={metrics.totalOrders.current}
          description="Last 30 days"
          icon={ShoppingBag}
          trend={metrics.totalOrders.percentageChange}
        />
        <MetricCard
          title="Active Users"
          value={metrics.totalUsers.current}
          description="Current users"
          icon={Users}
          trend={metrics.totalUsers.percentageChange}
        />
        <MetricCard
          title="Customer Retention"
          value={`${metrics.customerRetentionRate.current}%`}
          description="Last 30 days"
          icon={UserCheck}
          trend={metrics.customerRetentionRate.percentageChange}
        />
      </Grid>

      {/* Business Performance Charts */}
      <Grid cols={1} mdCols={2} gap={6}>
        <Card>
          <CardHeader>
            <CardTitle>Revenue Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={[
                { month: 'Jan', revenue: 250000 },
                { month: 'Feb', revenue: 265000 },
                { month: 'Mar', revenue: 283000 }
              ]}
              dataKey="revenue"
              xAxisKey="month"
              colors={['#6366F1']}
              title="Monthly Revenue"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Customer Growth</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="bar"
              data={[
                { month: 'Jan', users: 13500 },
                { month: 'Feb', users: 14000 },
                { month: 'Mar', users: 15000 }
              ]}
              dataKey="users"
              xAxisKey="month"
              colors={['#10B981']}
              title="Monthly Active Users"
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Re-commerce Performance */}
      <Grid cols={1} mdCols={2} gap={6}>
        <Card>
          <CardHeader>
            <CardTitle>Re-commerce Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="pie"
              data={[
                { name: 'Resale', value: 40 },
                { name: 'Repair', value: 25 },
                { name: 'Rental', value: 20 },
                { name: 'Recycle', value: 15 }
              ]}
              dataKey="value"
              xAxisKey="name"
              colors={['#6366F1', '#10B981', '#F59E0B', '#3B82F6']}
              title="Re-commerce Categories"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Environmental Impact</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={[
                { month: 'Jan', co2: 10.5 },
                { month: 'Feb', co2: 11.2 },
                { month: 'Mar', co2: 12.5 }
              ]}
              dataKey="co2"
              xAxisKey="month"
              colors={['#10B981']}
              title="CO₂ Savings (tons)"
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Service Performance Cards */}
      <Grid cols={1} mdCols={2} lgCols={4} gap={4}>
        <MetricCard
          title="Resale Items"
          value="1,245"
          description="Active listings"
          icon={Package}
          trend={12}
        />
        <MetricCard
          title="Active Repairs"
          value="89"
          description="In progress"
          icon={Wrench}
          trend={4}
        />
        <MetricCard
          title="Rental Usage"
          value="78%"
          description="Utilization rate"
          icon={Repeat}
          trend={8}
        />
        <MetricCard
          title="Recycle Rate"
          value="92%"
          description="Return rate"
          icon={Recycle}
          trend={12}
        />
      </Grid>
    </Stack>
  );
} 
